import PropTypes from 'prop-types';
import { useSiteContext, useTranslation } from '@eti/providers';
import { Badge, breakpoints, Card, Inline, Stack, Text, textVariants, vars } from '@etg/wings';
import { css } from '@eti/styles';
import { Accordion, Pictogram } from '@eti/components';
import { useOrderDetails } from '../../../../containers/OrderDetailsContext';
import { PURCHASE_OPTION_BY_TYPE } from './purchaseOptionConstants';

const accordionWrapperStyles = css`
  border: 1px solid ${vars.colors.divider};
  border-radius: 3px;
`;

const badgeStyles = css`
  @media (min-width: ${breakpoints._560}) {
    order: 1;
  }
`;

const numberBallStyles = css`
  background-color: ${vars.colors.decoration.dark};
  border-radius: 50%;
  color: #fff;
  font-size: 0.75rem;
  height: 20px;
  line-height: 20px;
  text-align: center;
  width: 20px;
`;

const outerStackStyles = css`
  @media (min-width: ${breakpoints._560}) {
    padding-inline-start: 48px;
  }
`;

const stepTextStyles = css`
  flex: 1;
`;

const PurchaseOption = ({ iconName, type }) => {
  const { t } = useTranslation();
  const { brand } = useSiteContext();
  const { numberOfSteps, isRecommended } = PURCHASE_OPTION_BY_TYPE[type];
  const { orderNumber } = useOrderDetails();
  const stepsToPurchaseCheckIn = [...Array(numberOfSteps)].map((_, index) =>
    t(`OrderDetails.CheckIn.NotPurchased.${type}.Step.${index + 1}`, orderNumber),
  );
  const badgeTitle = isRecommended ? t('OrderDetails.CheckIn.Options.Recommended') : null;

  return (
    <>
      <Card className={accordionWrapperStyles}>
        <Accordion id={`${type}-purchase-option`} spacing={16} variant="full-width">
          <Accordion.Header data-testid={`${type}-purchase-option`}>
            <Inline
              align="start"
              alignY="top"
              collapseBelow={breakpoints._560}
              spacing={{ [breakpoints._0]: 8, [breakpoints._560]: 16 }}
            >
              {badgeTitle && (
                <Badge className={badgeStyles} variant="promotional">
                  {badgeTitle}
                </Badge>
              )}
              <Inline alignY="center" noWrap>
                <Pictogram
                  brand={brand.code}
                  data-testid={`expansion-panel-header-${iconName}`}
                  id={iconName}
                  size="32px"
                />
                <Text variant={textVariants._16_BOLD}>
                  {t(`OrderDetails.CheckIn.${type}.Title`)}
                </Text>
              </Inline>
            </Inline>
          </Accordion.Header>
          <Accordion.Body>
            <Stack className={outerStackStyles}>
              <Text asHtml>{t(`OrderDetails.CheckIn.NotPurchased.${type}.Description`)}</Text>
              <Stack data-testid={`${type}-purchase-option-steps`} spacing={8}>
                {stepsToPurchaseCheckIn.map((step, index) => (
                  <Inline
                    alignY="center"
                    data-testid={`${type}-purchase-option-step-${index + 1}`}
                    key={`step-${index}`} // eslint-disable-line react/no-array-index-key
                    noWrap
                  >
                    <div className={numberBallStyles}>{index + 1}</div>
                    <Text asHtml className={stepTextStyles}>
                      {step}
                    </Text>
                  </Inline>
                ))}
              </Stack>
            </Stack>
          </Accordion.Body>
        </Accordion>
      </Card>
    </>
  );
};

PurchaseOption.propTypes = {
  iconName: PropTypes.string,
  type: PropTypes.string,
};

export default PurchaseOption;
